<template>
  <div class="delivery-availability-mail-container">
    <Card size="small">
      <template
        v-if="isSuccessful"
        #heading
      >
        <p><strong>Vielen Dank!</strong></p>
      </template>
      <template #default>
        <template v-if="isSuccessful">
          Du erhälst in Kürze eine E-Mail von uns, um deine Anfrage zu bestätigen.
        </template>
        <template v-else>
          Als Erstes informiert werden, sobald das Produkt (auch außerhalb eines möglichen
          Aktionszeitraums) wieder verfügbar ist – bitte E-Mail-Adresse hinterlegen:
          <LinkButton
            tag="button"
            type="button"
            asterix
            aria-label="Fußnote"
            @click="clickOpenModal()"
          >
            *
          </LinkButton>

          <FormErrorList
            ref="fehler"
            class="delivery-availability-mail-container__errors"
            :errors="parsedErrors"
          />
        </template>
      </template>
      <template #action>
        <form
          v-if="!isSuccessful"
          class="delivery-availability-mail-container__controls"
          @submit="submit"
        >
          <TextInput
            ref="daten"
            v-model="emailValue"
            class="delivery-availability-mail-container__input"
            label="E-Mail-Adresse"
            id-prefix="email"
            required
            :disabled="!isEditable"
          />
          <LinkButton
            tag="button"
            type="submit"
            class="delivery-availability-mail-container__button"
            primary
            :disabled="!isValidMail"
          >
            Nachricht erhalten
          </LinkButton>
        </form>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { openModal } from '~/composables/modal';
import { getErrorMessages } from '~/lib/error-parser';
import FormErrorList from '~/components/form-error-list.vue';
import { TextInput, LinkButton, Card } from '@i22team/td-longtail-vue-components';
import { tealiumLink } from '~/lib/tealium';

export type DeliveryAvailabilityMailContainerProps = {
  articleNumber: string;
};

const props = withDefaults(defineProps<DeliveryAvailabilityMailContainerProps>(), {});

const emailValue = ref<string | null>(null);
const parsedErrors = ref<string[] | null>(null);
const isEditable = ref(true);
const isSuccessful = ref(false);

const isValidMail = computed<boolean>(() => /(.*)@(.*)\.(.*)/.test(emailValue.value));

const clickOpenModal = () => {
  openModal({
    closeOnBackdrop: true,
    content: [
      'In dem Zusammenhang speichern wir ausschließlich Deine E-Mail-Adresse. Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 lit. f der Datenschutz-Grundverordnung. Die Daten werden ausschließlich in Deutschland verarbeitet.',
      'Die Löschung der Daten erfolgt nach spätestens 6 Monaten. Eine Nutzung der Daten für einen anderen als den vorgenannten Verwendungszweck oder eine Übermittlung an sonstige Dritte findet seitens der Telekom nur statt, sofern dies gesetzlich zulässig ist oder Du ausdrücklich eingewilligt hast.',
    ]
      .map((t) => `<p>${t}</p>`)
      .join(''),
  });
};

const submit = async (e) => {
  e.preventDefault();
  isSuccessful.value = false;
  if (!props.articleNumber || !emailValue.value) return;

  try {
    const subscription = {
      articleNumber: props.articleNumber,
      email: emailValue.value,
    };
    await useShopApi().availabilitySubscriptions(subscription);
    isSuccessful.value = true;
  } catch (error) {
    const { errors } = error.data;
    parsedErrors.value = getErrorMessages(errors);
  }
  tealiumLink({
    wt_link_id: 'content.button.nachricht-erhalten',
  });
};

defineExpose({
  submit,
  emailValue,
  parsedErrors,
  isSuccessful,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.delivery-availability-mail-container {
  margin-bottom: $grid-base * 2;

  :deep(.card) {
    --card-bg-color: transparent;
  }

  &__errors {
    padding: 0;
  }

  &__input {
    width: 100%;
  }

  &__controls {
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    @include mq($mq-sm) {
      flex-direction: row;
      align-items: flex-end;
    }
  }
}
</style>
